/* `/agents/{id}/reports` — what an agent told Builder it couldn't do (its
   `report_to_builder` calls), for its owner to read, fix with Builder and close.
   Only the Reports tab reads them, so they live with it. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "@/shared/api/client";
import { unwrap, type Envelope } from "@/shared/api/envelope";

import type { AgentReport, ReportStatus } from "@/shared/types";

export const reportKeys = {
  all: ["reports"] as const,
  agent: (agentId: string, status: ReportStatus | "all") => [...reportKeys.all, agentId, status] as const,
};

export async function listReports(agentId: string, status?: ReportStatus): Promise<AgentReport[]> {
  return unwrap(
    await client.get<Envelope<AgentReport[]>>(`/agents/${agentId}/reports`, { params: { status } }),
  );
}

export async function setReportStatus(agentId: string, id: string, status: ReportStatus): Promise<AgentReport> {
  return unwrap(await client.patch<Envelope<AgentReport>>(`/agents/${agentId}/reports/${id}`, { status }));
}

export function useReports(agentId: string, status?: ReportStatus) {
  return useQuery({
    queryKey: reportKeys.agent(agentId, status ?? "all"),
    queryFn: () => listReports(agentId, status),
    enabled: !!agentId,
  });
}

export function useSetReportStatus(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: ReportStatus }) => setReportStatus(agentId, v.id, v.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: reportKeys.all }),
  });
}
